"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
// Using custom SVG icons from assets
import CookieIcon from "@/assets/cookie.svg";
import XIcon from "@/assets/x.svg";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export const COOKIE_CONSENT_KEY = "articollect-cookie-consent";

export function CookieConsent() {
  const [showConsent, setShowConsent] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations("cookies");

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);

    // Show consent if no choice made OR if cookies were declined
    if (!consent || consent === "declined") {
      // Show consent after a short delay
      const timer = setTimeout(() => {
        setShowConsent(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    setShowConsent(false);
    setIsOpen(false);
    // Dispatch custom event to notify Google Analytics component
    window.dispatchEvent(
      new CustomEvent("cookieConsentChange", { detail: "accepted" }),
    );
    console.log("All cookies accepted");
  };

  const handleRequiredOnly = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "required-only");
    setShowConsent(false);
    setIsOpen(false);
    // Only essential cookies, disable analytics and marketing
    console.log("Required cookies only");
  };

  const handleDeclineAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "declined");
    setShowConsent(false);
    setIsOpen(false);
    // Disable all non-essential cookies
    console.log("All cookies declined");
  };

  const handleClose = () => {
    setShowConsent(false);
  };

  if (!showConsent) return null;

  return (
    <>
      {/* Simple banner for quick accept/decline */}
      <div className="fixed left-2 md:left-auto bottom-2 right-2 z-50 md:max-w-sm bg-white dark:bg-card border border-gray-200 dark:border-border shadow-lg p-4">
        <div className="flex items-start gap-3">
          <CookieIcon className="h-5 w-5 text-gray-600 dark:text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-gray-700 dark:text-foreground mb-3">
              {t("banner.message")}
            </p>
            <div className="flex flex-col gap-2">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs h-8">
                    {t("banner.learnMore")}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <CookieIcon className="h-5 w-5" />
                      {t("dialog.title")}
                    </SheetTitle>
                    <SheetDescription>
                      {t("dialog.description")}
                    </SheetDescription>
                  </SheetHeader>

                  <div className="mt-6 space-y-6">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-foreground mb-2">
                        {t("dialog.aboutTitle")}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-muted-foreground mb-4">
                        {t("dialog.aboutDescription")}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="border border-gray-200 dark:border-border p-4">
                        <h4 className="font-medium text-sm text-gray-900 dark:text-foreground mb-1">
                          {t("dialog.essentialTitle")}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-muted-foreground mb-2">
                          {t("dialog.essentialDescription")}
                        </p>
                        <div className="text-xs text-gray-500 dark:text-muted-foreground/70">
                          {t("dialog.alwaysActive")}
                        </div>
                      </div>

                      <div className="border border-gray-200 dark:border-border p-4">
                        <h4 className="font-medium text-sm text-gray-900 dark:text-foreground mb-1">
                          {t("dialog.analyticsTitle")}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-muted-foreground mb-2">
                          {t("dialog.analyticsDescription")}
                        </p>
                        <div className="text-xs text-gray-500 dark:text-muted-foreground/70">
                          {t("dialog.optional")}
                        </div>
                      </div>

                      <div className="border border-gray-200 dark:border-border p-4">
                        <h4 className="font-medium text-sm text-gray-900 dark:text-foreground mb-1">
                          {t("dialog.marketingTitle")}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-muted-foreground mb-2">
                          {t("dialog.marketingDescription")}
                        </p>
                        <div className="text-xs text-gray-500 dark:text-muted-foreground/70">
                          {t("dialog.optional")}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-border">
                      <Link
                        href="/privacy-policy#cookies"
                        className="text-sm text-blue-600 dark:text-primary hover:text-blue-800 dark:hover:text-primary/80 underline"
                        onClick={() => setIsOpen(false)}
                      >
                        {t("dialog.privacyLink")}
                      </Link>
                    </div>

                    <div className="flex flex-col gap-2 pt-4">
                      <Button
                        onClick={handleAccept}
                        className="w-full bg-black hover:bg-gray-800 dark:bg-primary dark:hover:bg-primary/90"
                      >
                        {t("dialog.acceptAll")}
                      </Button>
                      <Button
                        onClick={handleRequiredOnly}
                        variant="outline"
                        className="w-full"
                      >
                        {t("dialog.acceptRequired")}
                      </Button>
                      <Button
                        onClick={handleDeclineAll}
                        variant="outline"
                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-destructive dark:hover:text-destructive dark:hover:bg-destructive/10"
                      >
                        {t("dialog.declineAll")}
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              <div className="flex gap-2">
                <Button
                  onClick={handleAccept}
                  size="sm"
                  className="flex-1 bg-black hover:bg-gray-800 dark:bg-primary dark:hover:bg-primary/90"
                >
                  {t("banner.accept")}
                </Button>
                <Button onClick={handleDeclineAll} size="sm" className="flex-1">
                  {t("banner.decline")}
                </Button>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="h-6 w-6 min-w-0 min-h-0 p-0 hover:bg-gray-100 dark:hover:bg-accent"
          >
            <XIcon className="h-6 w-6" />
          </Button>
        </div>
      </div>
    </>
  );
}
